/**
 * AdminAssetDetailPage — Issue #364.
 *
 * Shows a single fixed asset with the immutable event ledger and the
 * lifecycle actions HR / admin needs:
 *   - assign / transfer
 *   - return
 *   - send to repair / complete repair
 *   - retire / mark lost / archive
 *
 * 红线 (AI GUIDE §6 / §11):
 *   - 所有状态切换通过乐观锁 expectedVersion；前端永远拿当前 row 的 version 提交。
 *   - 金额字段在没有 admin_assets:finance 权限时显示为占位符；retired / lost
 *     / archive 只允许持有 admin_assets:manage 的角色看到操作按钮。
 *   - 错误以 Card 提示；不构造假状态，所有写入都走 generated client。
 */
import { useEffect, useState } from 'react';
import { useRoute, Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import {
  AssociationLookupSelector,
  type SelectedAssociation,
} from '@/components/business/AssociationLookupSelector';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_ASSETS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAssetsPermissions';
import {
  ADMIN_ASSET_CATEGORY_LABELS,
  ADMIN_ASSET_DEPRECIATION_METHOD_LABELS,
  ADMIN_ASSET_STATUS_LABELS,
  useAdminAssetDetail,
  useAdminAssetMutations,
} from '@/hooks/useAdminAssets';

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN');
}

function fmtMoney(value: string | null | undefined, canSee: boolean): string {
  if (!canSee) return '受限';
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

export default function AdminAssetDetailPage() {
  const [, params] = useRoute<{ id: string }>('/admin/assets/:id');
  const id = params?.id ?? '';
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_ASSETS_PERMISSIONS.READ);
  const canWrite = hasPermission(permissions, ADMIN_ASSETS_PERMISSIONS.WRITE);
  const canManage = hasPermission(
    permissions,
    ADMIN_ASSETS_PERMISSIONS.MANAGE,
  );
  const canSeeFinance = hasPermission(
    permissions,
    ADMIN_ASSETS_PERMISSIONS.FINANCE,
  );

  const { item, events, error, fetchAsset, fetchEvents } =
    useAdminAssetDetail();
  const mutations = useAdminAssetMutations();

  // Audit P2: the assignment field uses the shared backend-backed employee
  // lookup. The selector keeps the resolved hr_employees.id around for submit
  // while the human-readable name renders in the input — admins never have to
  // type a raw employee UUID.
  const [employee, setEmployee] = useState<SelectedAssociation | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!id || !canRead) return;
    void fetchAsset(id);
    void fetchEvents(id);
  }, [id, canRead, fetchAsset, fetchEvents]);

  if (!canRead) return <AccessDenied resource="行政固定资产" />;
  if (!item) {
    return (
      <div className="p-6 text-sm text-slate-500">
        {error ? error.message : '加载中…'}
      </div>
    );
  }

  const refresh = async () => {
    await fetchAsset(id);
    await fetchEvents(id);
  };

  const handleAssign = async () => {
    if (!employee) return;
    const updated = await mutations.assignAsset(id, {
      expectedVersion: item.version,
      toEmployeeId: employee.id,
      reason: reason.trim() || undefined,
    });
    if (updated) {
      setEmployee(null);
      setReason('');
      await refresh();
    }
  };

  const handleReturn = async () => {
    const updated = await mutations.returnAsset(id, {
      expectedVersion: item.version,
      reason: reason.trim() || undefined,
    });
    if (updated) {
      setReason('');
      await refresh();
    }
  };

  const handleSendToRepair = async () => {
    const updated = await mutations.sendToRepair(id, {
      expectedVersion: item.version,
      reason: reason.trim() || undefined,
    });
    if (updated) {
      setReason('');
      await refresh();
    }
  };

  const handleCompleteRepair = async () => {
    const updated = await mutations.completeRepair(id, {
      expectedVersion: item.version,
      reason: reason.trim() || undefined,
    });
    if (updated) {
      setReason('');
      await refresh();
    }
  };

  const handleRetire = async () => {
    const updated = await mutations.retireAsset(id, {
      expectedVersion: item.version,
      reason: reason.trim() || undefined,
    });
    if (updated) {
      setReason('');
      await refresh();
    }
  };

  const handleMarkLost = async () => {
    if (!reason.trim()) return;
    const updated = await mutations.markLost(id, {
      expectedVersion: item.version,
      reason: reason.trim(),
    });
    if (updated) {
      setReason('');
      await refresh();
    }
  };

  const handleArchive = async () => {
    const updated = await mutations.archiveAsset(id, {
      expectedVersion: item.version,
      reason: reason.trim() || undefined,
    });
    if (updated) {
      setReason('');
      await refresh();
    }
  };

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">
            {item.assetCode} · {item.name}
          </h1>
          <p className="text-sm text-slate-500">
            类别 {ADMIN_ASSET_CATEGORY_LABELS[item.category]} · 折旧{' '}
            {ADMIN_ASSET_DEPRECIATION_METHOD_LABELS[item.depreciationMethod]} ·
            版本 v{item.version}
          </p>
        </div>
        <Link href="/admin/assets" className="text-sm text-blue-600 hover:underline">
          返回列表
        </Link>
      </header>

      {error || mutations.error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {(error ?? mutations.error)?.message}
        </Card>
      ) : null}

      <Card className="grid grid-cols-2 gap-4 p-4 text-sm">
        <div>
          <div className="text-xs text-slate-500">当前状态</div>
          <div className="font-semibold">
            {ADMIN_ASSET_STATUS_LABELS[item.status]}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500">当前保管人</div>
          <div className="font-mono text-xs" data-testid="admin-assets-custodian">
            {item.custodianEmployeeId ?? '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500">采购日期</div>
          <div>{fmtDate(item.purchaseDate)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">原值</div>
          <div>{fmtMoney(item.originalCost, canSeeFinance)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">累计折旧</div>
          <div>{fmtMoney(item.depreciationAmount, canSeeFinance)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">残值</div>
          <div>{fmtMoney(item.residualValue, canSeeFinance)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">存放地点</div>
          <div>{item.location ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">SN</div>
          <div className="font-mono text-xs">{item.serialNo ?? '—'}</div>
        </div>
        <div className="col-span-2">
          <div className="text-xs text-slate-500">备注</div>
          <div className="whitespace-pre-wrap text-xs">
            {item.notes ?? '—'}
          </div>
        </div>
      </Card>

      {canWrite || canManage ? (
        <Card className="space-y-3 p-4 text-sm">
          <div className="text-xs text-slate-500">资产生命周期操作</div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div
              className="md:w-72"
              data-testid="admin-assets-assign-employee"
            >
              <AssociationLookupSelector
                kind="employee"
                value={employee}
                onChange={setEmployee}
                placeholder="搜索员工姓名 / 工号"
                disabled={!canWrite || mutations.loading}
              />
            </div>
            <input
              type="text"
              placeholder="原因 (已自动脱敏)"
              className="flex-1 rounded border px-2 py-1 text-xs"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {canWrite ? (
              <>
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
                  disabled={mutations.loading || !employee}
                  onClick={handleAssign}
                  data-testid="admin-assets-assign-button"
                  title={employee ? undefined : '请先选择目标员工'}
                >
                  领用 / 转移
                </button>
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
                  disabled={mutations.loading}
                  onClick={handleReturn}
                  data-testid="admin-assets-return-button"
                >
                  归还入库
                </button>
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
                  disabled={mutations.loading}
                  onClick={handleSendToRepair}
                >
                  送修
                </button>
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
                  disabled={mutations.loading}
                  onClick={handleCompleteRepair}
                >
                  维修完成
                </button>
              </>
            ) : null}
            {canManage ? (
              <>
                <button
                  type="button"
                  className="rounded border border-amber-300 px-3 py-1 text-xs text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                  disabled={mutations.loading}
                  onClick={handleRetire}
                >
                  报废
                </button>
                <button
                  type="button"
                  className="rounded border border-red-300 px-3 py-1 text-xs text-red-800 hover:bg-red-50 disabled:opacity-50"
                  disabled={mutations.loading || !reason.trim()}
                  onClick={handleMarkLost}
                  title={!reason.trim() ? '遗失必须填写原因' : undefined}
                >
                  标记遗失
                </button>
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-xs text-slate-500 hover:bg-muted disabled:opacity-50"
                  disabled={mutations.loading}
                  onClick={handleArchive}
                >
                  归档 (软删除)
                </button>
              </>
            ) : null}
          </div>
        </Card>
      ) : null}

      <Card className="p-4">
        <h2 className="mb-2 text-sm font-semibold">事件流水</h2>
        {events.length === 0 ? (
          <div className="text-xs text-slate-500" data-testid="admin-assets-events-empty">
            暂无事件
          </div>
        ) : (
          <ol className="space-y-2 text-xs">
            {events.map((e) => (
              <li
                key={e.id}
                className="rounded border border-slate-100 p-2 text-slate-700"
                data-testid={`admin-assets-event-${e.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono">{e.action}</span>
                  <span className="text-slate-400">{fmtDate(e.occurredAt)}</span>
                </div>
                <div className="text-slate-500">
                  {(e.fromStatus ?? '—')} → {(e.toStatus ?? '—')}
                </div>
                {e.fromEmployeeId || e.toEmployeeId ? (
                  <div className="text-slate-500">
                    {(e.fromEmployeeId ?? '—')} → {(e.toEmployeeId ?? '—')}
                  </div>
                ) : null}
                {e.reason ? <div className="italic">{e.reason}</div> : null}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
